import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MunicipalitySelect } from "@/components/MunicipalitySelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useSortableData, SortableHead } from "@/hooks/useSortableData";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Search, Pencil, Trash2, Inbox } from "lucide-react";
import { toast } from "sonner";
import { ExportButton } from "@/components/ExportButton";

const COMPLAINT_TYPES = ["Development and Panchayati Raj", "Social Welfare & Scheme Deliveries", "Public Health & Sanitation", "Irrigation & Electricity", "Police & Law Enforcement", "Urban Local Bodies (ULB)"];
const EMPTY = { date: "", ward_no: "", complaint_type: "", total_registered: "", total_resolved: "" };

export default function JanSamwad() {
  const { hasPerm } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [muni, setMuni] = useState({ district: "", name: "", type: "" });
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const canManage = hasPerm("jan_samwad.manage");

  const load = () => {
    setLoading(true);
    api.get("/jan-samwad").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return items;
    return items.filter((it) => [it.district, it.municipality_name, it.municipality_type, it.ward_no, it.complaint_type].some((v) => (v || "").toString().toLowerCase().includes(q)));
  }, [items, search]);

  const livePct = useMemo(() => {
    const t = Number(form.total_registered), r = Number(form.total_resolved);
    return t > 0 ? Math.round((r / t) * 10000) / 100 : 0;
  }, [form.total_registered, form.total_resolved]);

  const openNew = () => { setForm(EMPTY); setMuni({ district: "", name: "", type: "" }); setEditing({}); };
  const openEdit = (it) => {
    setForm({ date: it.date || "", ward_no: it.ward_no ?? "", complaint_type: it.complaint_type || "", total_registered: it.total_registered ?? "", total_resolved: it.total_resolved ?? "" });
    setMuni({ district: it.district || "", name: it.municipality_name || "", type: it.municipality_type || "" });
    setEditing(it);
  };
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!muni.district || !muni.name) { toast.error("Select district and municipal name"); return; }
    setSaving(true);
    const payload = {
      district: muni.district,
      municipality_name: muni.name,
      municipality_type: muni.type,
      date: form.date || null,
      ward_no: form.ward_no === "" ? null : Number(form.ward_no),
      complaint_type: form.complaint_type || null,
      total_registered: form.total_registered === "" ? null : Number(form.total_registered),
      total_resolved: form.total_resolved === "" ? null : Number(form.total_resolved),
    };
    try {
      if (editing && editing.id) await api.put(`/jan-samwad/${editing.id}`, payload);
      else await api.post("/jan-samwad", payload);
      toast.success(editing && editing.id ? "Updated" : "Added");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try { await api.delete(`/jan-samwad/${deleteItem.id}`); toast.success("Deleted"); setDeleteItem(null); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const num = (v) => (v == null || v === "" ? "—" : Number(v).toLocaleString("en-IN"));

  const sort = useSortableData(filtered);
  const sorted = sort.sorted;

  return (
    <div data-testid="jan-samwad-page">
      <div className="flex justify-between items-start mb-6 gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-600 font-medium">Monitoring</p>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">Jan Samwad Resolution</h1>
          <p className="text-sm text-slate-500 mt-1">{items.length} record(s) of Jan Samwad complaint resolution.</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton items={items} filename="jan-samwad" testId="jan-samwad-export-btn" />
          {canManage && (
          <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="jan-samwad-add-btn">
            <Plus className="h-4 w-4 mr-2" /> Add New
          </Button>
          )}
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <div className="p-4 border-b border-slate-200">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by district, municipality, ward, type..." className="pl-9" data-testid="jan-samwad-search-input" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">S.No</TableHead>
                <SortableHead column="district" sort={sort}>District</SortableHead>
                <SortableHead column="municipality_name" sort={sort}>Municipality</SortableHead>
                <SortableHead column="municipality_type" sort={sort}>Type</SortableHead>
                <SortableHead column="date" sort={sort}>Date</SortableHead>
                <SortableHead column="ward_no" sort={sort} className="text-right" right>Ward</SortableHead>
                <SortableHead column="complaint_type" sort={sort}>Complaint Type</SortableHead>
                <SortableHead column="total_registered" sort={sort} className="text-right" right>Registered</SortableHead>
                <SortableHead column="total_resolved" sort={sort} className="text-right" right>Resolved</SortableHead>
                <SortableHead column="percentage" sort={sort} className="text-right" right>Percentage</SortableHead>
                {canManage && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={canManage ? 11 : 10} className="text-center py-10 text-slate-400">Loading...</TableCell></TableRow>
              ) : sorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManage ? 11 : 10} className="text-center py-12">
                    <Inbox className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500">No records yet.</p>
                  </TableCell>
                </TableRow>
              ) : (
                sorted.map((it, idx) => (
                  <TableRow key={it.id} className="hover:bg-slate-50" data-testid={`jan-samwad-row-${it.id}`}>
                    <TableCell className="text-slate-500">{idx + 1}</TableCell>
                    <TableCell className="capitalize">{(it.district || "—").toLowerCase()}</TableCell>
                    <TableCell className="font-medium">{it.municipality_name || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-600">{it.municipality_type || "—"}</TableCell>
                    <TableCell>{it.date ? new Date(it.date).toLocaleDateString() : "—"}</TableCell>
                    <TableCell className="text-right">{it.ward_no ?? "—"}</TableCell>
                    <TableCell className="text-xs">{it.complaint_type || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{num(it.total_registered)}</TableCell>
                    <TableCell className="text-right tabular-nums">{num(it.total_resolved)}</TableCell>
                    <TableCell className="text-right">
                      <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 font-semibold px-2.5 py-1 text-xs">{it.percentage != null ? `${it.percentage}%` : "—"}</span>
                    </TableCell>
                    {canManage && (
                      <TableCell className="text-right whitespace-nowrap">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(it)} data-testid={`jan-samwad-edit-${it.id}`}><Pencil className="h-4 w-4 text-slate-600" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteItem(it)} data-testid={`jan-samwad-delete-${it.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="jan-samwad-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing && editing.id ? "Edit Record" : "Add Record"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <MunicipalitySelect value={muni} onChange={setMuni} testId="jan-samwad-muni" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setField("date", e.target.value)} data-testid="jan-samwad-input-date" />
              </div>
              <div className="space-y-1.5">
                <Label>Ward Number</Label>
                <Input type="number" value={form.ward_no} onChange={(e) => setField("ward_no", e.target.value)} data-testid="jan-samwad-input-ward" />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label>Type of Complaint</Label>
                <Select value={form.complaint_type} onValueChange={(v) => setField("complaint_type", v)}>
                  <SelectTrigger data-testid="jan-samwad-input-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {COMPLAINT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Total Jan Samwad Complaint Registered</Label>
                <Input type="number" value={form.total_registered} onChange={(e) => setField("total_registered", e.target.value)} data-testid="jan-samwad-input-registered" />
              </div>
              <div className="space-y-1.5">
                <Label>Total Jan Samwad Complaint Resolved</Label>
                <Input type="number" value={form.total_resolved} onChange={(e) => setField("total_resolved", e.target.value)} data-testid="jan-samwad-input-resolved" />
              </div>
              <div className="space-y-1.5">
                <Label>Percentage (auto)</Label>
                <Input value={`${livePct}%`} readOnly className="bg-slate-50 font-semibold text-emerald-700" data-testid="jan-samwad-input-percentage" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="jan-samwad-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="jan-samwad-save-btn">{saving ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteItem} onOpenChange={(o) => !o && setDeleteItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this record?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="jan-samwad-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-500 hover:bg-red-600" data-testid="jan-samwad-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
